import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { ToastAction } from '@/components/ui/toast';
import { toast } from '@/hooks/use-toast';
import { iaJobPath, listRecentVideoJobs, type DocAiJobStatus } from '@/lib/docAiVideo';

// Aviso no app quando um vídeo do "Criar com IA" termina (D1): o usuário pode
// ter fechado o diálogo e ido para outra tela. Montado no RequireAuth, então
// vale em qualquer página logada. O e-mail sai do doc-ai-video-callback.

const POLL_MS = 30_000;
const TERMINAL: DocAiJobStatus[] = ['pronto', 'erro'];

export function VideoJobNotifier() {
  const navigate = useNavigate();
  const seen = useRef<Map<string, DocAiJobStatus> | null>(null);
  const { data } = useQuery({
    queryKey: ['doc-ai-jobs-recent'],
    queryFn: listRecentVideoJobs,
    refetchInterval: (q) => (q.state.data?.some((j) => !TERMINAL.includes(j.status)) ? POLL_MS : POLL_MS * 4),
    refetchIntervalInBackground: true,
    staleTime: POLL_MS / 2,
  });

  useEffect(() => {
    if (!data) return;
    const prev = seen.current;
    const next = new Map(data.map((j) => [j.id, j.status]));
    seen.current = next;
    // Primeira leitura só registra: não avisa de jobs que já tinham terminado.
    if (!prev) return;
    for (const j of data) {
      const before = prev.get(j.id);
      if (!before || TERMINAL.includes(before) || !TERMINAL.includes(j.status)) continue;
      const nome = j.draft?.name || j.fileName || 'vídeo';
      toast({
        title: j.status === 'pronto' ? 'Documento do vídeo pronto' : 'O processamento do vídeo falhou',
        description: j.status === 'pronto'
          ? `${nome}: a prévia está pronta para revisar.`
          : `${nome}: ${j.error ?? 'tente enviar de novo.'}`,
        variant: j.status === 'erro' ? 'destructive' : undefined,
        duration: 15_000,
        action: (
          <ToastAction altText="Abrir" onClick={() => navigate(iaJobPath(j.id))} data-testid="ia-video-aviso-abrir">
            Abrir
          </ToastAction>
        ),
      });
    }
  }, [data, navigate]);

  return null;
}
